import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Tabs, Input, Select, Button, Space, Typography, message } from 'antd';
import { SearchOutlined, CloseOutlined, FilterOutlined, TagOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@src/app/store';
import {
  closeSnippetsDrawer,
  updateSearchConfig,
  setSearchResults,
  setLoading,
  setError,
} from '@src/app/reducers/snippets/snippetsSlice';
import useTranslation from '@hooks/useTranslation';
import { SnippetsSearchRequest, SnippetEntity } from '@kemu-io/kemu-types';
import { SNIPPET_CATEGORIES, colorByCategoryName, decodeSvgFromBase64 } from '@src/common/constants/snippetCategories';
import { searchSnippets } from '@src/api/snippet/snippetApi';
import { UISnippet } from '@src/app/reducers/snippets/snippetsSlice';
import SnippetsList from './SnippetsList/SnippetsList';
import SnippetDetail from './SnippetDetail/SnippetDetail';
import styles from './SnippetsDrawer.module.css';
import { SITE_LAYOUT_ELEMENT_CLASS } from '@common/constants';

const { Search } = Input;
const { Option } = Select;

/**
 * Main drawer component for browsing and viewing Kemu snippets
 * @returns React.JSX.Element
 */
const SnippetsDrawer = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const t = useTranslation('Snippets.Drawer');
  const cw = t.withBaseKey('CommonWords');
  
  const {
    isDrawerVisible,
    currentView,
    searchConfig,
  } = useSelector((state: RootState) => state.snippets);

  const [searchQuery, setSearchQuery] = useState(searchConfig.query || '');
  const [activeTab, setActiveTab] = useState<'community' | 'my-snippets' | 'organization'>('community');
  const [isDraggingSnippet, setIsDraggingSnippet] = useState(false);

  /**
   * Handles closing the drawer
   */
  const handleClose = useCallback(() => {
    dispatch(closeSnippetsDrawer());
  }, [dispatch]);

  /**
   * Loads snippets based on current search configuration
   */
  const loadSnippets = useCallback(async () => {
    const append = Boolean(searchConfig.page && searchConfig.page > 1);
    dispatch(setLoading(!append));
    dispatch(setError(null));
    
    try {
      const response = await searchSnippets(searchConfig);
      
      // Transform SnippetEntity[] to UISnippet[]
      const uiSnippets: UISnippet[] = response.snippets.map((snippet: SnippetEntity) => {
        const uiSnippet: UISnippet = {
          ...snippet,
          category: {
            name: snippet.category || 'Uncategorized',
            color: colorByCategoryName(snippet.category as typeof SNIPPET_CATEGORIES[number]),
          },
          iconSvg: snippet.iconSvg ? decodeSvgFromBase64(snippet.iconSvg) : '',
        }

        return uiSnippet;
      });
      
      // console.log('uiSnippets', uiSnippets);
      dispatch(setSearchResults({
        snippets: uiSnippets,
        total: response.total,
        hasMore: response.hasMore,
        append,
      }));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || t('Results.Error');
      dispatch(setError(errorMessage));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, searchConfig]);

  /**
   * Handles search input change
   */
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  /**
   * Handles search submission
   */
  const handleSearch = useCallback((value: string) => {
    dispatch(updateSearchConfig({ query: value }));
  }, [dispatch]);

  /**
   * Handles category filter change
   */
  const handleCategoryChange = useCallback((value: string) => {
    dispatch(updateSearchConfig({ category: value || undefined }));
    // loadSnippets();
  }, [dispatch]);

  // /**
  //  * Handles featured filter toggle
  //  */
  // const handleFeaturedChange = useCallback((checked: boolean) => {
  //   dispatch(updateSearchConfig({ featured: checked }));
  // }, [dispatch]);

  /**
   * Handles sort order change
   */
  const handleSortChange = useCallback((value: string) => {
    dispatch(updateSearchConfig({ sortBy: value as SnippetsSearchRequest['sortBy'] }));
  }, [dispatch]);

  /**
   * Handles snippet dragging state changes
   * @param isDragging - Whether a snippet is currently being dragged
   */
  const handleSnippetDragging = useCallback((isDragging: boolean) => {
    setIsDraggingSnippet(isDragging);
  }, []);

  /**
   * Handles tab change between Community, My Snippets, and Organization
   */
  const handleTabChange = useCallback((key: string) => {
    let visibility: 'public' | 'private' | 'organization';
    let tabKey: 'community' | 'my-snippets' | 'organization';
    
    switch (key) {
      case 'community':
        visibility = 'public';
        tabKey = 'community';
        break;
      case 'my-snippets':
        visibility = 'private';
        tabKey = 'my-snippets';
        break;
      case 'organization':
        visibility = 'organization';
        tabKey = 'organization';
        break;
      default:
        visibility = 'public';
        tabKey = 'community';
    }
    
    
    setActiveTab(tabKey);
    
    // Update visibility based on tab selection
    const updateData: { visibility: 'public' | 'private' | 'organization'; page: number; sortBy?: SnippetsSearchRequest['sortBy'] } = {
      visibility,
      page: 1, // reset pagination
    };
    
    // Set default sort to 'most_recent' for My Snippets tab
    if (tabKey === 'my-snippets') {
      updateData.sortBy = 'most_recent';
    }
    
    dispatch(updateSearchConfig(updateData));
    
  }, [dispatch]);


  /**
   * Loads snippets when drawer opens
   */
  useEffect(() => {
    if (isDrawerVisible) {
      loadSnippets();
    }
  }, [isDrawerVisible, loadSnippets]);

  /**
   * Syncs search query with Redux state
   */
  useEffect(() => {
    setSearchQuery(searchConfig.query || '');
  }, [searchConfig.query]);

  const textBySortValue = useCallback((value: string) => {
    switch (value) {
      case 'most_recent':
        return t('Filters.SortBy.MostRecent');
      case 'highest_rated':
        return t('Filters.SortBy.HighestRated');
      case 'alphabetical':
        return t('Filters.SortBy.Alphabetical');
    }
  }, [t]);

  const renderHeader = () => (
    <div className={styles.Header}>
      <div className={styles.HeaderLeft}>
        <Typography.Title level={4} className={styles.Title}>
          {t('Title')}
        </Typography.Title>
      </div>
      <div className={styles.HeaderRight}>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={handleClose}
          title={t('Close')}
        />
      </div>
    </div>
  );

  const renderSearchAndFilters = () => (
    <div className={styles.SearchAndFilters}>
      {/* Search Bar */}
      <div className={styles.SearchContainer}>
        <Search
          placeholder={t('Search.Placeholder')}
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          onSearch={handleSearch}
          enterButton={<SearchOutlined />}
          size="large"
          autoFocus
          className={styles.SearchInput}
        />
      </div>

      {/* Filters */}
      <div className={styles.FiltersContainer}>
        <Space wrap size="small" className={styles.Filters}>
          {/* Category Filter */}
          <Select
            dropdownStyle={{ minWidth: 220 }}
            popupClassName={styles.SelectDropdown}
            placeholder={t('Filters.Category')}
            value={searchConfig.category || ''}
            onChange={handleCategoryChange}
            title={t('Filters.Category')}
            style={{ minWidth: 120 }}
            suffixIcon={<FilterOutlined />}
          >
            <Option value="">{t('Filters.Category.All')}</Option>
            {SNIPPET_CATEGORIES.map(category => (
              <Option key={category} value={category}>
                {category}
              </Option>
            ))}
          </Select>

          {/* Featured Filter */}
          {/* <Select
            placeholder={t('Filters.Featured')}
            value={searchConfig.featured ? 'featured' : ''}
            onChange={(value) => handleFeaturedChange(value === 'featured')}
            style={{ minWidth: 100 }}
          >
            <Option value="">{cw('All')}</Option>
            <Option value="featured">{t('Filters.Featured')}</Option>
          </Select> */}

          {/* Recommended Filter */}
          {/* <Select
            placeholder={t('Filters.Recommended')}
            value={searchConfig.recommended ? 'recommended' : ''}
            onChange={(value) => handleRecommendedChange(value === 'recommended')}
            style={{ minWidth: 120 }}
          >
            <Option value="">{cw('All')}</Option>
            <Option value="recommended">{t('Filters.Recommended')}</Option>
          </Select> */}

          {/* Sort Filter */}
          <Select
            popupClassName={styles.SelectDropdown}
            placeholder={t('Filters.SortBy')}
            title={t('Filters.SortBy')}
            value={textBySortValue(searchConfig.sortBy || 'most_recent')}
            onChange={handleSortChange}
            style={{ minWidth: 140 }}
            suffixIcon={<TagOutlined />}
          >
            <Option value="most_recent">{textBySortValue('most_recent')}</Option>
            <Option value="highest_rated">{textBySortValue('highest_rated')}</Option>
            <Option value="alphabetical">{textBySortValue('alphabetical')}</Option>
          </Select>
        </Space>
      </div>
    </div>
  );

  const renderContent = () => {
    if (currentView === 'detail') {
      return <SnippetDetail />;
    }

    return (
      <div className={styles.Content}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: 'community',
              label: t('Tabs.Community'),
            },
            {
              key: 'my-snippets',
              label: t('Tabs.MySnippets'),
            },
            {
              key: 'organization',
              label: t('Tabs.Organization'),
            },
          ]}
          className={styles.Tabs}
        />
        <SnippetsList key={activeTab} onSnippetDragging={handleSnippetDragging} />
      </div>
    );
  };

  return (
    <Drawer
      title={renderHeader()}
      placement="left"
      width={600}
      open={isDrawerVisible}
      mask={false}
      getContainer={document.querySelector(`.${SITE_LAYOUT_ELEMENT_CLASS}`) as HTMLElement}
      onClose={handleClose}
      closable={false}
      rootClassName={`${styles.DrawerRoot} ${isDraggingSnippet ? styles.DrawerRootDragging : ''}`}
      className={styles.Drawer}
      classNames={{
        wrapper: styles.DrawerWrapper,
        body: styles.DrawerBody,
      }}
    >
      <div className={styles.DrawerContent}>
        {renderSearchAndFilters()}
        {renderContent()}
      </div>
    </Drawer>
  );
};

export default SnippetsDrawer;
